import { reactive, readonly } from 'vue';
import axios from 'axios';
import { brandy, locations } from '@/api';
import { ClientService } from '@/services/client';
import { MenuService } from '@/services/menu';
import { NavigatorService } from '@/services/navigator';
import { SentryService } from '@/services/sentry';
import { NATIVE_APP_AGENT } from '@/utils/constants';
import { isSourceValid } from '@/utils/functions';
import { SOURCES } from '@/types/enums/common';
import type { BrandAppInfo } from '@/types/interfaces/brands';

/**
 * Решение «показывать гейт партнёрского приложения»: бренд и ссылка на установку
 */
export interface PartnerAppGateInfo {
	/** id бренда, по атрибутам которого принято решение (effective.id) */
	brandId: number;
	brandName: string;
	downloadUrl: string;
}

export interface PartnerAppGateState {
	/** Филиал текущего роута, для которого посчитан gate */
	filialId: number | null;
	/** Поток A — гейт по бренду филиала на takeaway-web.choco.kz */
	gate: PartnerAppGateInfo | null;
	/** Поток B — гейт по бренду партнёрского домена (config.brand_id) */
	domainGate: PartnerAppGateInfo | null;
}

/** Таймаут запроса бренда по config.brand_id: дальше — fallback на контракт */
export const BRAND_BY_CONFIG_TIMEOUT_MS = 1500;

const HTTP_NOT_FOUND = 404;

const state = reactive<PartnerAppGateState>({
	filialId: null,
	gate: null,
	domainGate: null
});

/**
 * Кэш решений по филиалу на время жизни вкладки — один запрос в Brandy на филиал.
 * Кэшируем только ответы Brandy (в т.ч. 404 «локация без бренда»); сетевые
 * ошибки не кэшируем, чтобы разовый сбой не выключил гейт до конца сессии
 */
const gateByFilial = new Map<number, PartnerAppGateInfo | null>();

/**
 * Летящие резолвы по филиалу — повторная навигация не порождает второй запрос
 */
const pendingByFilial = new Map<number, Promise<PartnerAppGateInfo | null>>();

const isPartnerAppAgent = () => navigator.userAgent.includes(NATIVE_APP_AGENT);

const isTabletSource = (source?: string | null) =>
	!!source && isSourceValid(source, SOURCES.TABLET, true);

/**
 * Планшет партнёра: source приходит в query только на первом заходе — Menu
 * вычищает его из URL, поэтому основной источник тот же, что у остальных
 * планшетных проверок (DefaultLayout, kiosk-url) — сохранённые данные меню
 */
const isTablet = (source?: string) =>
	isTabletSource(source) ||
	isTabletSource(MenuService.state.tempFilialData.source);

/**
 * Обёртки, где гейт не показываем никогда: партнёрская нативка (UA ChocoPartnerApp),
 * суперапп Rahmet (window.RahmetApp), планшет партнёра в зале (source TABLET*)
 */
const isExcluded = (source?: string): boolean =>
	isPartnerAppAgent() || ClientService.isWebView() || isTablet(source);

const getGateFromBrand = (
	brand?: BrandAppInfo | null
): PartnerAppGateInfo | null => {
	if (!brand?.web_app_only || !brand.app_download_url) {
		return null;
	}

	return {
		brandId: brand.id,
		brandName: brand.name,
		downloadUrl: brand.app_download_url
	};
};

/**
 * 404 приходит либо AxiosError-ом, либо телом api-proxy (`error_code`), когда
 * шлюз отвечает 200 и интерцептор реджектит сырой конверт
 */
const isNotFoundError = (error: unknown) => {
	if (axios.isAxiosError(error)) {
		return error.response?.status === HTTP_NOT_FOUND;
	}

	return (
		typeof error === 'object' &&
		error !== null &&
		(error as { error_code?: number }).error_code === HTTP_NOT_FOUND
	);
};

const toError = (error: unknown) => {
	if (error instanceof Error) {
		return error;
	}

	const message =
		typeof error === 'object' && error !== null
			? JSON.stringify(error)
			: String(error);

	return new Error(message);
};

/**
 * Fail-open: ошибка Brandy = «у филиала нет приложения». 404 — штатный ответ
 * «локация не привязана к бренду», в Sentry его не шлём (интерцептор axios
 * и так фиксирует все API-ошибки)
 */
const reportError = (context: string, error: unknown) => {
	console.error(`[PartnerAppGate] ${context}:`, error);

	if (isNotFoundError(error)) {
		return;
	}

	SentryService.captureException(toError(error));
};

/**
 * UUID локации филиала. Карточка филиала обычно уже загружена страницей —
 * тогда запроса нет; иначе берём карточку терминала сами (один раз на филиал)
 */
const fetchLocationUuid = async (filialId: number): Promise<string> => {
	const recent = NavigatorService.state.recentFilial;

	if (recent?.terminal_id === filialId && recent.id) {
		return recent.id;
	}

	const response = await locations.getFilial({ filial_id: filialId });

	return response.data.location.id;
};

const resolveGate = (filialId: number) => {
	const request = fetchLocationUuid(filialId)
		.then(async locationUuid => {
			const { effective } = await brandy.getBrandByLocation(locationUuid);
			const gate = getGateFromBrand(effective);

			gateByFilial.set(filialId, gate);

			return gate;
		})
		.catch(error => {
			reportError(`gate for filial ${filialId} failed`, error);

			// «локация без бренда» — стабильный ответ, его кэшируем
			if (isNotFoundError(error)) {
				gateByFilial.set(filialId, null);
			}

			return null;
		})
		.finally(() => pendingByFilial.delete(filialId));

	pendingByFilial.set(filialId, request);

	return request;
};

/**
 * Поток A: решение для филиала текущего роута. Филиал фиксируется синхронно,
 * поэтому уход со страницы филиала (clear) или переход на другой филиал всегда
 * «сильнее» ещё летящего ответа — он попадёт только в кэш
 */
const setCurrentFilial = async (filialId: number): Promise<void> => {
	state.filialId = filialId;

	if (gateByFilial.has(filialId)) {
		state.gate = gateByFilial.get(filialId) ?? null;
		return;
	}

	state.gate = null;

	const gate = await (pendingByFilial.get(filialId) ?? resolveGate(filialId));

	if (state.filialId !== filialId) {
		return;
	}

	state.gate = gate;
};

/**
 * Роут без филиала — баннер по филиалу за пределами его страниц не живёт.
 * domainGate не трогаем: он от роута не зависит
 */
const clear = (): void => {
	state.filialId = null;
	state.gate = null;
};

const rejectAfter = (ms: number) => {
	let timer: ReturnType<typeof setTimeout> | undefined;

	const promise = new Promise<never>((_, reject) => {
		timer = setTimeout(
			() => reject(new Error(`PartnerAppGate: brand request timeout ${ms}ms`)),
			ms
		);
	});

	return { promise, cancel: () => clearTimeout(timer) };
};

/**
 * Поток B: гейт по бренду партнёрского домена. Fail-open, как и у филиала:
 * ошибка или таймаут Brandy — сайт открывается. Таймаут лишь ограничивает,
 * сколько гость ждёт под лоадером
 */
const resolveByConfig = async (brandId: string | number): Promise<void> => {
	const timeout = rejectAfter(BRAND_BY_CONFIG_TIMEOUT_MS);

	try {
		const brand = await Promise.race([
			brandy.getBrand(brandId),
			timeout.promise
		]);
		state.domainGate = getGateFromBrand(brand);
	} catch (error) {
		reportError(`brand ${brandId} by config failed`, error);
		state.domainGate = null;
	} finally {
		timeout.cancel();
	}
};

export const PartnerAppGate = {
	state: readonly(state) as PartnerAppGateState,
	isExcluded,
	setCurrentFilial,
	clear,
	resolveByConfig
};
